/**
 * MCP Simulation Tools
 *
 * Tools for observing and controlling EnergyPlus simulations.
 * These tools provide real-time access to simulation status, logs, and results.
 */

import type { MCPTool } from '../types';

/**
 * Get simulation status
 */
const getSimulationStatus: MCPTool = {
  name: 'get_simulation_status',
  description: `Get the current status of the EnergyPlus simulation including status (idle, loading, ready, running, complete, error, cancelled), current phase, progress percentage, and time estimates. Use this to check if a simulation is running or to see results.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Get simulation logs
 */
const getSimulationLogs: MCPTool = {
  name: 'get_simulation_logs',
  description: `Get console output lines from the simulation. Can filter by severity type (info, warning, severe, fatal, success) and limit the number of lines returned. Use this to monitor simulation progress or investigate issues.`,
  inputSchema: {
    type: 'object',
    properties: {
      filter: {
        type: 'string',
        description: 'Filter by line type: "all", "problems" (warnings and errors only), "info", "warning", "severe", "fatal", "success"',
        enum: ['all', 'problems', 'info', 'warning', 'severe', 'fatal', 'success'],
      },
      limit: {
        type: 'number',
        description: 'Maximum number of lines to return (default: 100, max: 1000)',
      },
      offset: {
        type: 'number',
        description: 'Number of lines to skip from the end (for pagination, default: 0)',
      },
    },
  },
};

/**
 * Get simulation errors
 */
const getSimulationErrors: MCPTool = {
  name: 'get_simulation_errors',
  description: `Get parsed error and warning entries from the simulation ERR file. Returns categorized errors with severity, message, context, and counts. Only available after simulation completes.`,
  inputSchema: {
    type: 'object',
    properties: {
      severity: {
        type: 'string',
        description: 'Filter by severity: "all", "warning", "severe", "fatal"',
        enum: ['all', 'warning', 'severe', 'fatal'],
      },
      limit: {
        type: 'number',
        description: 'Maximum number of entries to return (default: 50)',
      },
    },
  },
};

/**
 * Get simulation results summary
 */
const getSimulationResults: MCPTool = {
  name: 'get_simulation_results',
  description: `Get a summary of available simulation results including output variables, error counts, and whether the simulation was successful. Use this after a simulation completes to understand what data is available.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Get output variables list
 */
const getOutputVariables: MCPTool = {
  name: 'get_output_variables',
  description: `List all output variables available from the simulation results. Returns variable IDs, names, key values (zone/surface names), units, and report frequency. Use this to discover what time series data can be queried.`,
  inputSchema: {
    type: 'object',
    properties: {
      search: {
        type: 'string',
        description: 'Search filter for variable names (case-insensitive)',
      },
      keyValue: {
        type: 'string',
        description: 'Filter by key value (e.g., zone name)',
      },
      limit: {
        type: 'number',
        description: 'Maximum number of variables to return (default: 50)',
      },
    },
  },
};

/**
 * Get time series data
 */
const getTimeSeriesData: MCPTool = {
  name: 'get_time_series_data',
  description: `Get time series data for a specific output variable. Returns timestamped values. Use get_output_variables first to find the variable ID you need.`,
  inputSchema: {
    type: 'object',
    properties: {
      variableId: {
        type: 'number',
        description: 'The variable ID from get_output_variables',
      },
      startIndex: {
        type: 'number',
        description: 'Start index for pagination (default: 0)',
      },
      limit: {
        type: 'number',
        description: 'Maximum number of data points to return (default: 100, max: 1000)',
      },
    },
    required: ['variableId'],
  },
};

/**
 * Prepare simulation
 */
const prepareSimulation: MCPTool = {
  name: 'prepare_simulation',
  description: `Load the current IDF model into the simulation engine. This must be called before start_simulation if the model hasn't been loaded yet. Loads the WASM engine if needed and prepares the model for simulation.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Start simulation
 */
const startSimulation: MCPTool = {
  name: 'start_simulation',
  description: `Start an EnergyPlus simulation with the current IDF model. The simulation runs in a web worker using EnergyPlus WASM. Monitor progress with get_simulation_status and get_simulation_logs. Call prepare_simulation first if hasInput is false.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Cancel simulation
 */
const cancelSimulation: MCPTool = {
  name: 'cancel_simulation',
  description: `Cancel a running simulation. Use forceCancel=true if the simulation appears hung and soft cancel doesn't work (this terminates the worker and requires WASM reload).`,
  inputSchema: {
    type: 'object',
    properties: {
      forceCancel: {
        type: 'boolean',
        description: 'Force terminate the worker (use only if soft cancel fails)',
      },
    },
  },
};

/**
 * Get simulation progress
 */
const getSimulationProgress: MCPTool = {
  name: 'get_simulation_progress',
  description: `Get detailed progress information for a running simulation including current phase (initializing, warmup, sizing, simulation, postprocess, complete, error), progress percentage, elapsed time, and estimated remaining time.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

export const simulationTools: MCPTool[] = [
  prepareSimulation,
  getSimulationStatus,
  getSimulationLogs,
  getSimulationErrors,
  getSimulationResults,
  getOutputVariables,
  getTimeSeriesData,
  startSimulation,
  cancelSimulation,
  getSimulationProgress,
];
